import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import { Move, Redo2, Undo2 } from 'lucide-react';
import { CAROUSEL_FONTS, DEFAULT_CAROUSEL_FONT_ID } from '../../../config/carouselFonts';

// Конфиг для очистки HTML внутри карточек
export const domPurifyConfig: import('dompurify').Config = {
  ALLOWED_TAGS: [
    'div', 'span', 'p', 'header', 'footer', 'main', 'section', 'article',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark',
    'sup', 'sub',
    'ul', 'ol', 'li',
    'code', 'pre', 'blockquote',
    'br', 'hr',
    'a',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'img',
    // SVG элементы
    'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse',
    'g', 'defs', 'use', 'text', 'tspan', 'textPath', 'clipPath', 'mask',
    'linearGradient', 'radialGradient', 'stop', 'pattern', 'image',
  ],
  ALLOWED_ATTR: [
    'style', 'src', 'alt',
    // SVG атрибуты
    'viewBox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y',
    'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
    'd', 'cx', 'cy', 'r', 'rx', 'ry', 'points', 'x1', 'y1', 'x2', 'y2',
    'transform', 'opacity', 'fill-opacity', 'stroke-opacity',
    'clip-path', 'mask', 'id', 'class',
  ],
  FORBID_TAGS: [
    'script', 'style', 'link', 'canvas', 'video', 'audio', 'source',
    'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'option', 'button',
    'meta', 'base',
  ],
  FORBID_ATTR: ['on*', 'href', 'srcset'],
  RETURN_TRUSTED_TYPE: false
};

// Утилиты для работы со шрифтами и HTML

// Нормализует названия шрифтов в HTML - добавляет кавычки для шрифтов с пробелами
// Использует HTML entities (&quot;) для безопасной вставки в атрибуты
export const normalizeFontNamesInHtml = (html: string): string => {
  const fontNames = [
    'Playfair Display Cyrillic', 'Playfair Display', 'Great Vibes', 
    'Shantell Sans', 'Oswald', 'Rubik', 
    'Science Gothic', 'Open Sans', 'Fira Sans', 'Russo One', 'Geologica',
    'Montserrat', 'Caveat'
  ];
  
  let normalized = html;
  
  // Сначала исправляем уже сломанные атрибуты style (где кавычки разорвали строку)
  // Паттерн: style="... font-family: " fontname", ..." -> style="... font-family: &quot;fontname&quot;, ..."
  fontNames.forEach(fontName => {
    // Исправляем сломанные атрибуты вида: font-family: " fontname", 
    const brokenPattern = new RegExp(
      `(font-family\\s*:\\s*)"\\s*${fontName.replace(/\s+/g, '\\s+')}\\s*"`,
      'gi'
    );
    normalized = normalized.replace(brokenPattern, `font-family: &quot;${fontName}&quot;`);
    
    // Исправляем сломанные атрибуты вида: font-family: " eb="" garamond",=""
    const veryBrokenPattern = new RegExp(
      `(font-family\\s*:\\s*)"\\s*${fontName.split(' ').join('\\s*"\\s*=\\s*""\\s*')}\\s*"`,
      'gi'
    );
    normalized = normalized.replace(veryBrokenPattern, `font-family: &quot;${fontName}&quot;`);
  });
  
  // Теперь нормализуем правильные варианты
  fontNames.forEach(fontName => {
    // Используем HTML entity &quot; вместо прямых кавычек для безопасности в атрибутах
    const quotedFontName = `&quot;${fontName}&quot;`;
    
    // Заменяем font-family: Font Name на font-family: &quot;Font Name&quot;
    // Учитываем разные варианты: с кавычками, без кавычек, с одинарными кавычками, с HTML entities
    const patterns = [
      // Без кавычек: font-family: Font Name,
      new RegExp(`(font-family\\s*:\\s*)${fontName.replace(/\s+/g, '\\s+')}(?=[,;\\s])`, 'gi'),
      // С одинарными кавычками: font-family: 'Font Name',
      new RegExp(`(font-family\\s*:\\s*)'${fontName.replace(/\s+/g, '\\s+')}'(?=[,;\\s])`, 'gi'),
      // С прямыми кавычками (уже есть, но может быть сломано): font-family: "Font Name",
      new RegExp(`(font-family\\s*:\\s*)"${fontName.replace(/\s+/g, '\\s+')}"(?=[,;\\s])`, 'gi'),
      // С HTML entities (уже нормализовано): font-family: &quot;Font Name&quot;,
      new RegExp(`(font-family\\s*:\\s*)&quot;${fontName.replace(/\s+/g, '\\s+')}&quot;(?=[,;\\s])`, 'gi'),
    ];
    
    patterns.forEach(pattern => {
      normalized = normalized.replace(pattern, `$1${quotedFontName}`);
    });
  });
  
  return normalized;
};

export const extractPrimaryFontsFromHtml = (html: string): string[] => {
  const fonts = new Set<string>();
  const matches = html.match(/font-family\s*:\s*([^;"]+)/gi) ?? [];
  for (const m of matches) {
    const value = m.split(':')[1]?.trim() ?? '';
    const first = value.split(',')[0]?.trim() ?? '';
    const cleaned = first.replace(/^['"]|['"]$/g, '').trim();
    if (cleaned) fonts.add(cleaned);
  }
  return Array.from(fonts);
};

export const ensureFontsLoaded = async (families: string[]) => {
  const fontSet = (document as any).fonts as FontFaceSet | undefined;
  if (!fontSet || typeof fontSet.load !== 'function') {
    return;
  }
  if (!families.length) return;
  
  // Ждем загрузки CSS со шрифтами
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Загружаем все шрифты
  await Promise.allSettled(
    families.map(async (fontFamily) => {
      try {
        // Для "Playfair Display Cyrillic" используем "Playfair Display"
        const fontToLoad = fontFamily === 'Playfair Display Cyrillic' ? 'Playfair Display' : fontFamily;
        
        // Загружаем обычный и жирный варианты
        await Promise.all([
          fontSet.load(`400 16px "${fontToLoad}"`).catch(() => {}),
          fontSet.load(`700 16px "${fontToLoad}"`).catch(() => {})
        ]);
      } catch (e) {
        // Игнорируем ошибки - браузер попытается использовать шрифт из CSS
      }
    })
  );
  
  await fontSet.ready;
};

interface RichModalEditorProps {
  open: boolean;
  html: string;
  onClose: () => void;
  onSave: (nextHtml: string) => void;
  isSaving?: boolean;
  isCover?: boolean; // Флаг, если это обложка Reels (другой аспект)
}

export const RichModalEditor: React.FC<RichModalEditorProps> = ({ 
  open, html, onClose, onSave, isSaving, isCover = false 
}) => {
  if (!open) return null;

  const editorRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const [localFontsReady, setLocalFontsReady] = useState(false);
  
  // Загружаем все шрифты из CAROUSEL_FONTS при открытии редактора
  useEffect(() => {
    if (!open) return;
    const run = async () => {
      try {
        const allFontFamilies = CAROUSEL_FONTS.map(f => f.fontFamily);
        await ensureFontsLoaded(allFontFamilies);
      } catch (e) {
        // Игнорируем ошибки
      }
    };
    void run();
  }, [open]);
  const [moveMode, setMoveMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  const [overlayRect, setOverlayRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [textColor, setTextColor] = useState('#ffffff');
  const [fontSize, setFontSize] = useState<number>(44);
  const [selectedFontIdLocal, setSelectedFontIdLocal] = useState(DEFAULT_CAROUSEL_FONT_ID);
  const [isBoldActive, setIsBoldActive] = useState(false);
  const [isItalicActive, setIsItalicActive] = useState(false);
  const [isUnderlineActive, setIsUnderlineActive] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const lastSnapshotAtRef = useRef<number>(0);

  const canvasWidth = 1080;
  const canvasHeight = isCover ? 1920 : 1350;

  // Рассчитываем ширину модалки так, чтобы она не была слишком высокой
  const maxAvailableHeight = windowSize.height * 0.65; // 65% от высоты экрана
  let modalWidth = Math.min(windowSize.width < 768 ? 320 : 500, windowSize.width - 24);

  // Если высота при такой ширине превышает лимит, уменьшаем ширину
  if (modalWidth * (canvasHeight / canvasWidth) > maxAvailableHeight) {
    modalWidth = maxAvailableHeight * (canvasWidth / canvasHeight);
  }

  const modalScale = modalWidth / canvasWidth;
  const modalHeight = modalWidth * (canvasHeight / canvasWidth);

  const sanitizedDraft = useMemo(() => DOMPurify.sanitize(html, domPurifyConfig), [html]);
  const lockedCanvasRootRef = useRef<HTMLElement | null>(null);

  const updateOverlay = () => {
    if (!shellRef.current || !selectedElement) {
      setOverlayRect(null);
      return;
    }
    const shell = shellRef.current.getBoundingClientRect();
    const el = selectedElement.getBoundingClientRect();
    setOverlayRect({
      left: el.left - shell.left,
      top: el.top - shell.top,
      width: el.width,
      height: el.height
    });
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        await ensureFontsLoaded(extractPrimaryFontsFromHtml(sanitizedDraft));
      } catch { /* ignore */ } finally {
        if (!cancelled) setLocalFontsReady(true);
      }
    };
    setLocalFontsReady(false);
    void run();
    return () => { cancelled = true; };
  }, [sanitizedDraft]);

  useEffect(() => {
    if (!open) return;
    const draft = sanitizedDraft || '';
    setUndoStack([draft]);
    setRedoStack([]);
    lastSnapshotAtRef.current = Date.now();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      const mod = navigator.platform.toLowerCase().includes('mac') ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo(); else handleUndo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onAny = () => {
      updateOverlay();
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onAny);
    window.addEventListener('scroll', onAny, true);
    return () => {
      window.removeEventListener('resize', onAny);
      window.removeEventListener('scroll', onAny, true);
    };
  }, [open, selectedElement]);

  useEffect(() => { updateOverlay(); }, [selectedElement]);

  const isSelectionInsideEditor = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !editorRef.current) return false;
    return editorRef.current.contains(sel.getRangeAt(0).commonAncestorContainer);
  };

  const rgbToHex = (rgb: string) => {
    const m = rgb.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
    if (!m) return null;
    const to2 = (n: string) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0');
    return `#${to2(m[1])}${to2(m[2])}${to2(m[3])}`;
  };

  const normalizeFontFamily = (family: string) => (family || '').split(',')[0]?.trim()?.replace(/^['"]|['"]$/g, '')?.toLowerCase();

  const syncToolbarFromSelection = () => {
    const sel = window.getSelection();
    if (!editorRef.current || !sel || sel.rangeCount === 0) return;
    const node = sel.getRangeAt(0).startContainer;
    const el = (node instanceof HTMLElement ? node : node.parentElement) as HTMLElement | null;
    if (!el || !editorRef.current.contains(el)) return;
    const cs = window.getComputedStyle(el);

    const hex = rgbToHex(cs.color);
    if (hex) setTextColor(hex);

    const sizePx = Number.parseFloat(cs.fontSize || '');
    if (!Number.isNaN(sizePx)) setFontSize(Math.round(sizePx));

    const fam = normalizeFontFamily(cs.fontFamily || '');
    if (fam) {
      const match = CAROUSEL_FONTS.find(f => f.fontFamily.toLowerCase() === fam || f.label.toLowerCase() === fam);
      if (match) setSelectedFontIdLocal(match.id);
    }

    const fw = Number.parseInt(cs.fontWeight || '400', 10);
    setIsBoldActive(fw >= 600);
    setIsItalicActive(cs.fontStyle === 'italic');
    setIsUnderlineActive((cs.textDecorationLine || '').includes('underline'));
  };

  useEffect(() => {
    if (!open) return;
    const onSel = () => syncToolbarFromSelection();
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const wrapSelectionWithSpan = (styles: Record<string, string>) => {
    const sel = window.getSelection();
    if (!sel || !editorRef.current || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;

    // Если применяется font-family, упрощаем структуру - удаляем вложенные span с font-family
    if (styles['font-family']) {
      
      // Сохраняем выделение
      const savedRange = range.cloneRange();
      
      // Находим все элементы внутри выделения
      const allElements: HTMLElement[] = [];
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_ELEMENT,
        null
      );
      
        let node: Node | null = walker.nextNode();
        while (node) {
          if (node instanceof HTMLElement && node.tagName === 'SPAN') {
            // Проверяем, что элемент внутри выделения
            if (range.intersectsNode(node)) {
              // Проверяем, есть ли inline style с font-family
              const inlineFontFamily = node.style.fontFamily;
              if (inlineFontFamily && inlineFontFamily.trim()) {
                allElements.push(node);
              }
            }
          }
          node = walker.nextNode();
        }
      
      // Сортируем по глубине (от внутренних к внешним), чтобы не сломать структуру
      allElements.sort((a, b) => {
        let depthA = 0, depthB = 0;
        let cur: HTMLElement | null = a;
        while (cur && cur !== editorRef.current) { depthA++; cur = cur.parentElement; }
        cur = b;
        while (cur && cur !== editorRef.current) { depthB++; cur = cur.parentElement; }
        return depthB - depthA; // Сначала удаляем самые глубокие
      });
      
      
      // Удаляем все span с font-family, заменяя их содержимым
      allElements.forEach(span => {
        if (span.parentNode && span.parentNode !== editorRef.current) {
          // Сохраняем содержимое
          const fragment = document.createDocumentFragment();
          while (span.firstChild) {
            fragment.appendChild(span.firstChild);
          }
          // Заменяем span его содержимым
          span.parentNode.replaceChild(fragment, span);
        }
      });
      
      // Восстанавливаем выделение после удаления span
      try {
        // Обновляем range, так как структура изменилась
        const newRange = document.createRange();
        const startContainer = savedRange.startContainer;
        const endContainer = savedRange.endContainer;
        
        // Если контейнеры - это текстовые узлы, используем их
        if (startContainer.nodeType === Node.TEXT_NODE && endContainer.nodeType === Node.TEXT_NODE) {
          newRange.setStart(startContainer, savedRange.startOffset);
          newRange.setEnd(endContainer, savedRange.endOffset);
        } else {
          // Иначе пытаемся найти текстовые узлы внутри
          const textNodes: Text[] = [];
          const textWalker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_TEXT,
            null
          );
          let textNode: Node | null = textWalker.nextNode();
          while (textNode) {
            if (textNode instanceof Text && range.intersectsNode(textNode)) {
              textNodes.push(textNode);
            }
            textNode = textWalker.nextNode();
          }
          
          if (textNodes.length > 0) {
            newRange.setStart(textNodes[0], 0);
            const lastNode = textNodes[textNodes.length - 1];
            newRange.setEnd(lastNode, lastNode.textContent?.length || 0);
          } else {
            // Fallback - используем сохраненный range
            newRange.setStart(savedRange.startContainer, savedRange.startOffset);
            newRange.setEnd(savedRange.endContainer, savedRange.endOffset);
          }
        }
        
        sel.removeAllRanges();
        sel.addRange(newRange);
        // Обновляем range для дальнейшего использования
        range.setStart(newRange.startContainer, newRange.startOffset);
        range.setEnd(newRange.endContainer, newRange.endOffset);
      } catch (e) {
        // Пытаемся восстановить простое выделение
        try {
          const textNode = savedRange.startContainer;
          if (textNode.nodeType === Node.TEXT_NODE) {
            const newRange = document.createRange();
            newRange.selectNodeContents(textNode);
            sel.removeAllRanges();
            sel.addRange(newRange);
            range.setStart(newRange.startContainer, newRange.startOffset);
            range.setEnd(newRange.endContainer, newRange.endOffset);
          }
        } catch {
          console.error('Критическая ошибка при восстановлении выделения');
        }
      }
    }

    // Создаем новый span с нужными стилями
    const span = document.createElement('span');
    Object.entries(styles).forEach(([k, v]) => {
      span.style.setProperty(k, v);
    });
    
    try {
      // Проверяем, что range все еще валиден
      if (range.collapsed) {
        return;
      }
      
      const contents = range.extractContents();
      if (!contents || contents.childNodes.length === 0) {
        return;
      }
      
      
      span.appendChild(contents);
    range.insertNode(span);
      
      // Выделяем новый span
    sel.removeAllRanges();
    const nextRange = document.createRange();
    nextRange.selectNodeContents(span);
    sel.addRange(nextRange);
      
    } catch (e) {
      console.error('❌ Ошибка при применении стилей:', e);
      console.error('Range:', {
        collapsed: range.collapsed,
        startContainer: range.startContainer?.nodeName,
        endContainer: range.endContainer?.nodeName
      });
    }
  };

  const toggleInlineStyle = (styleKey: string, styleValue: string) => {
    if (!isSelectionInsideEditor()) return;
    if (['font-weight', 'font-style', 'text-decoration'].includes(styleKey)) {
      const cmd = styleKey === 'font-weight' ? 'bold' : styleKey === 'font-style' ? 'italic' : 'underline';
      try { document.execCommand(cmd); } catch { /* ignore */ }
      syncToolbarFromSelection();
      return;
    }
    wrapSelectionWithSpan({ [styleKey]: styleValue });
  };

  const onEditorClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!moveMode || !editorRef.current) return;
    if (!lockedCanvasRootRef.current) lockedCanvasRootRef.current = editorRef.current.firstElementChild as HTMLElement;
    const candidates = ((document.elementsFromPoint?.(e.clientX, e.clientY) ?? []) as HTMLElement[])
      .filter(el => editorRef.current?.contains(el) && el !== editorRef.current && el !== lockedCanvasRootRef.current && !el.contains(lockedCanvasRootRef.current as any));

    if (candidates.length === 0) { setSelectedElement(null); return; }

    const shellRect = shellRef.current?.getBoundingClientRect();
    if (!shellRect) return;

    const chosen = candidates
      .map(el => {
        const r = el.getBoundingClientRect();
        let depth = 0, cur: HTMLElement | null = el;
        while (cur && cur !== editorRef.current) { depth++; cur = cur.parentElement; }
        return { el, area: r.width * r.height, depth, w: r.width / shellRect.width, h: r.height / shellRect.height };
      })
      .filter(x => x.area > 0 && x.area / (shellRect.width * shellRect.height) < 0.55 && x.w < 0.92 && x.h < 0.92)
      .sort((a, b) => b.depth !== a.depth ? b.depth - a.depth : a.area - b.area)[0]?.el ?? null;

    setSelectedElement(chosen);
  };

  const setElementTranslate = (el: HTMLElement, x: number, y: number) => {
    const current = el.style.transform || '';
    const m = current.match(/translate\(\s*(-?\d+(?:\.\d+)?)px\s*,\s*(-?\d+(?:\.\d+)?)px\s*\)/i);
    const rest = m ? current.replace(m[0], '').trim() : current;
    el.style.transform = `${rest}${rest ? ' ' : ''}translate(${Math.round(x)}px, ${Math.round(y)}px)`.trim();
  };

  const handleDragPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!selectedElement) return;
    const el = selectedElement, startX = e.clientX, startY = e.clientY;
    const m = (el.style.transform || '').match(/translate\(\s*(-?\d+(?:\.\d+)?)px\s*,\s*(-?\d+(?:\.\d+)?)px\s*\)/i);
    const baseX = Number(m?.[1] ?? 0), baseY = Number(m?.[2] ?? 0);

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      setElementTranslate(el, baseX + (ev.clientX - startX) / modalScale, baseY + (ev.clientY - startY) / modalScale);
      updateOverlay();
    };
    const onUp = () => {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp);
      updateOverlay();
    };
    window.addEventListener('pointermove', onMove); window.addEventListener('pointerup', onUp);
  };

  const pushSnapshot = (reason: 'beforeEdit' | 'typing') => {
    if (!editorRef.current) return;
    const now = Date.now();
    if (reason === 'typing' && now - lastSnapshotAtRef.current < 350) return;
    lastSnapshotAtRef.current = now;
    const next = editorRef.current.innerHTML;
    setUndoStack(prev => prev[prev.length - 1] === next ? prev : [...(prev.length >= 50 ? prev.slice(1) : prev), next]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (!editorRef.current || undoStack.length <= 1) return;
    const cur = editorRef.current.innerHTML, nextStack = undoStack.slice(0, -1);
    editorRef.current.innerHTML = nextStack[nextStack.length - 1] ?? '';
    setRedoStack(r => [...r, cur]); setUndoStack(nextStack);
    setSelectedElement(null); setTimeout(() => updateOverlay(), 0);
  };

  const handleRedo = () => {
    if (!editorRef.current || redoStack.length === 0) return;
    const currentHtml = editorRef.current.innerHTML;
    const next = redoStack[redoStack.length - 1] ?? '';
    const nextRedo = redoStack.slice(0, -1);
    editorRef.current.innerHTML = next;
    setUndoStack(u => [...u, currentHtml]);
    setRedoStack(nextRedo);
    setSelectedElement(null);
    setTimeout(() => updateOverlay(), 0);
  };

  const modalContent = (
    <div className="fixed inset-0 z-[20000]">
      <div className="absolute inset-0 bg-black/60" aria-label="Закрыть редактор" onClick={onClose} />
      <div className="absolute inset-0 flex items-stretch md:items-center justify-center p-0 md:p-3">
        <div className="w-full h-full md:h-auto md:max-w-5xl bg-white dark:bg-gray-900 rounded-none md:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Редактор карточки</div>
          </div>
          <div className="p-3 md:p-4 flex-1 overflow-auto">
            <div ref={shellRef} className="relative border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-black overflow-hidden mx-auto" style={{ width: modalWidth, height: modalHeight }}>
              <div
                ref={editorRef} contentEditable={!moveMode} suppressContentEditableWarning
                onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
                onClick={onEditorClick} onMouseUp={syncToolbarFromSelection} onKeyUp={syncToolbarFromSelection} onInput={() => pushSnapshot('typing')}
                className="outline-none carousel-card-content"
                style={{ width: canvasWidth, height: canvasHeight, transform: `scale(${modalScale})`, transformOrigin: 'top left', visibility: localFontsReady ? 'visible' : 'hidden' }}
                dangerouslySetInnerHTML={{ __html: sanitizedDraft }}
              />
              {!localFontsReady && <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">Загружаем шрифты…</div>}
              {moveMode && overlayRect && <div onPointerDown={handleDragPointerDown} className="absolute border-2 border-blue-500 cursor-move bg-blue-500/10" style={{ ...overlayRect }} />}
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-800 px-3 py-3 bg-white/80 dark:bg-gray-900/80 backdrop-blur">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar whitespace-nowrap flex-nowrap">
              <input type="color" value={textColor} onChange={e => { setTextColor(e.target.value); pushSnapshot('beforeEdit'); toggleInlineStyle('color', e.target.value); }} className="shrink-0 w-11 h-10 rounded-xl p-1" />
              <input type="number" min={10} max={240} value={fontSize} onChange={e => { setFontSize(Number(e.target.value)); pushSnapshot('beforeEdit'); toggleInlineStyle('font-size', `${e.target.value}px`); }} className="shrink-0 w-16 h-10 px-2 text-sm rounded-xl" />
              <select value={selectedFontIdLocal} onChange={(e) => { 
                setSelectedFontIdLocal(e.target.value); 
                const f = CAROUSEL_FONTS.find(x => x.id === e.target.value); 
                if (f) { 
                  pushSnapshot('beforeEdit'); 
                  
                  const sel = window.getSelection();
                  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
                    return;
                  }
                  
                  // Применяем шрифт - используем правильное экранирование для шрифтов с пробелами
                  const fontFamilyValue = f.fontFamily.includes(' ') 
                    ? `"${f.fontFamily}", system-ui, sans-serif`
                    : `${f.fontFamily}, system-ui, sans-serif`;
                  
                  wrapSelectionWithSpan({ 'font-family': fontFamilyValue }); 
                  
                  // Обновляем тулбар
                  setTimeout(() => syncToolbarFromSelection(), 50);
                } 
              }} className="shrink-0 h-10 px-3 text-sm rounded-xl">
                {CAROUSEL_FONTS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <div className="shrink-0 w-px h-7 bg-gray-300 dark:bg-gray-700" />
              <button type="button" onClick={() => { pushSnapshot('beforeEdit'); toggleInlineStyle('font-weight', '800'); }} className={`shrink-0 h-10 w-10 rounded-xl border ${isBoldActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>B</button>
              <button type="button" onClick={() => { pushSnapshot('beforeEdit'); toggleInlineStyle('font-style', 'italic'); }} className={`shrink-0 h-10 w-10 rounded-xl border ${isItalicActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>I</button>
              <button type="button" onClick={() => { pushSnapshot('beforeEdit'); toggleInlineStyle('text-decoration', 'underline'); }} className={`shrink-0 h-10 w-10 rounded-xl border ${isUnderlineActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>U</button>
              <div className="shrink-0 w-px h-7 bg-gray-300 dark:bg-gray-700" />
              <button type="button" onClick={() => setMoveMode(v => !v)} className={`shrink-0 h-10 w-10 flex items-center justify-center rounded-xl border ${moveMode ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}><Move className="w-4 h-4" /></button>
              <button type="button" onClick={handleUndo} disabled={undoStack.length <= 1} className="shrink-0 h-10 w-10 flex items-center justify-center disabled:opacity-30"><Undo2 className="w-4 h-4" /></button>
              <button type="button" onClick={handleRedo} disabled={redoStack.length === 0} className="shrink-0 h-10 w-10 flex items-center justify-center disabled:opacity-30"><Redo2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="h-10 px-4 text-sm rounded-xl border border-gray-300">Закрыть</button>
              <button type="button" onClick={() => editorRef.current && onSave(editorRef.current.innerHTML)} disabled={isSaving} className="h-10 px-4 text-sm rounded-xl bg-blue-600 text-white">{isSaving ? 'Сохранение…' : 'Сохранить'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

